import { BrandGroupEntity } from '@/entities/brand-group.entity';
import { createClientLogger } from '@/client/logging/client-logger';
import { ProductGroupEntity } from '@/entities/product-group.entity';
import { ReviewEntity } from '@/entities/review.entity';
import { Sort } from '@/types/sort.type';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMarketplaceApi } from '@/context/marketplace.context';
import { readStoredReviewPage } from '@/context/reviews-page-transport.helpers';

type UseReviewsPageFetchTransportArgs = {
    clientId: string;
    accessToken: string;
    reviewsOnPage: number;
    filtersQuery: string[];
    sort: Sort;
    setProductsState: (value: ProductGroupEntity[]) => void;
    setBrandGroupsState: (value: BrandGroupEntity[]) => void;
    setReviewsAndTotalState: (reviews: ReviewEntity[], total: number) => void;
};

type ReviewsPageFetchTransport = {
    getProducts: (abort?: AbortSignal) => Promise<void>;
    getBrandGroups: (abort?: AbortSignal) => Promise<void>;
    refetchReviews: (abort?: AbortSignal) => Promise<void>;
    refetchReviewsAndProducts: (abort?: AbortSignal) => Promise<void>;
    isReviewsRefetchRunning: boolean;
};

const reviewsPageLogger = createClientLogger({
    scope: 'reviews-page',
});

export const useReviewsPageFetchTransport = ({
    clientId,
    accessToken,
    reviewsOnPage,
    filtersQuery,
    sort,
    setProductsState,
    setBrandGroupsState,
    setReviewsAndTotalState,
}: UseReviewsPageFetchTransportArgs): ReviewsPageFetchTransport => {
    const { reviewsApi, productsApi, brandsApi } = useMarketplaceApi();
    const [isReviewsRefetchRunning, setIsReviewsRefetchRunning] = useState(false);
    const refetchAbortControllerRef = useRef<AbortController | null>(null);
    const refetchRequestIdRef = useRef(0);
    const logIgnoredRefetchResponse = useCallback(
        ({
            requestId,
            includeGroups,
            reason,
        }: {
            requestId: number;
            includeGroups: boolean;
            reason: 'request_aborted' | 'request_replaced';
        }) => {
            reviewsPageLogger.trace('[client.reviews-page.refetch] Ignored stale refetch response.', {
                requestId,
                includeGroups,
                reason,
                activeRequestId: refetchRequestIdRef.current,
            });
        },
        [],
    );

    useEffect(() => {
        return () => {
            if (refetchAbortControllerRef.current) {
                refetchAbortControllerRef.current.abort();
                refetchAbortControllerRef.current = null;
            }
        };
    }, []);

    const getProducts = useCallback(
        async (signal?: AbortSignal) => {
            const productsResponse = await productsApi.getProducts({
                signal,
                authToken: accessToken,
            });

            setProductsState(productsResponse.data);
        },
        [accessToken, productsApi, setProductsState],
    );

    const getBrandGroups = useCallback(
        async (signal?: AbortSignal) => {
            const brandGroupsResponse = await brandsApi.getBrandGroups({
                signal,
                authToken: accessToken,
            });

            setBrandGroupsState(brandGroupsResponse.data);
        },
        [accessToken, brandsApi, setBrandGroupsState],
    );

    const executeRefetch = useCallback(
        async ({ signal, includeGroups }: { signal?: AbortSignal; includeGroups: boolean }) => {
            if (refetchAbortControllerRef.current) {
                reviewsPageLogger.trace('[client.reviews-page.refetch] Replaced in-flight refetch request.', {
                    requestId: refetchRequestIdRef.current + 1,
                    includeGroups,
                    replacedRequestId: refetchRequestIdRef.current,
                });
                refetchAbortControllerRef.current.abort();
            }

            const refetchAbortController = new AbortController();
            refetchAbortControllerRef.current = refetchAbortController;
            const requestId = refetchRequestIdRef.current + 1;
            refetchRequestIdRef.current = requestId;
            setIsReviewsRefetchRunning(true);
            const abortCurrentRefetch = () => {
                refetchAbortController.abort();
            };
            if (signal) {
                if (signal.aborted) {
                    reviewsPageLogger.trace(
                        '[client.reviews-page.refetch] Started refetch with an already-aborted outer signal.',
                        {
                            requestId,
                            includeGroups,
                        },
                    );
                    refetchAbortController.abort();
                } else {
                    signal.addEventListener('abort', abortCurrentRefetch, { once: true });
                }
            }

            const page = readStoredReviewPage(clientId);
            reviewsPageLogger.debug('[client.reviews-page.refetch] refetch start', {
                page,
                reviewsOnPage,
                filtersQuery,
                sort,
                includeGroups,
            });
            let resolvedRefetchResponse = false;
            try {
                const reviewsPromise = reviewsApi.getReviews(
                    {
                        skip: (page - 1) * reviewsOnPage,
                        take: reviewsOnPage,
                        search: filtersQuery,
                        ...sort,
                    },
                    { signal: refetchAbortController.signal, authToken: accessToken },
                );

                if (!includeGroups) {
                    reviewsPageLogger.trace(
                        '[client.reviews-page.refetch] Skipped grouped page fetch for reviews-only refetch.',
                        {
                            requestId,
                        },
                    );
                    const reviewsResponse = await reviewsPromise;
                    resolvedRefetchResponse = true;

                    if (refetchAbortController.signal.aborted) {
                        logIgnoredRefetchResponse({
                            requestId,
                            includeGroups,
                            reason: 'request_aborted',
                        });
                        return;
                    }

                    if (refetchRequestIdRef.current !== requestId) {
                        logIgnoredRefetchResponse({
                            requestId,
                            includeGroups,
                            reason: 'request_replaced',
                        });
                        return;
                    }

                    setReviewsAndTotalState(reviewsResponse.data.data, reviewsResponse.data.total);
                    reviewsPageLogger.debug('[client.reviews-page.refetch] refetch done', {
                        includeGroups,
                        total: reviewsResponse.data.total,
                        reviews: reviewsResponse.data.data.length,
                    });
                    return;
                }

                const [reviewsResponse, groupsResponse] = await Promise.all([
                    reviewsPromise,
                    reviewsApi.getReviewsPageGroups({
                        signal: refetchAbortController.signal,
                        authToken: accessToken,
                    }),
                ]);
                resolvedRefetchResponse = true;

                if (refetchAbortController.signal.aborted) {
                    logIgnoredRefetchResponse({
                        requestId,
                        includeGroups,
                        reason: 'request_aborted',
                    });
                    return;
                }

                if (refetchRequestIdRef.current !== requestId) {
                    logIgnoredRefetchResponse({
                        requestId,
                        includeGroups,
                        reason: 'request_replaced',
                    });
                    return;
                }

                setReviewsAndTotalState(reviewsResponse.data.data, reviewsResponse.data.total);
                setProductsState(groupsResponse.data.products);
                setBrandGroupsState(groupsResponse.data.brandGroups);
                reviewsPageLogger.debug('[client.reviews-page.refetch] refetch done', {
                    includeGroups,
                    total: reviewsResponse.data.total,
                    reviews: reviewsResponse.data.data.length,
                });
            } finally {
                if (!resolvedRefetchResponse && refetchAbortController.signal.aborted) {
                    reviewsPageLogger.trace(
                        '[client.reviews-page.refetch] Aborted refetch request before completion.',
                        {
                            requestId,
                            includeGroups,
                        },
                    );
                }

                if (signal) {
                    signal.removeEventListener('abort', abortCurrentRefetch);
                }

                if (refetchAbortControllerRef.current === refetchAbortController) {
                    refetchAbortControllerRef.current = null;
                    setIsReviewsRefetchRunning(false);
                }
            }
        },
        [
            accessToken,
            clientId,
            filtersQuery,
            reviewsApi,
            reviewsOnPage,
            setBrandGroupsState,
            setProductsState,
            setReviewsAndTotalState,
            sort,
            logIgnoredRefetchResponse,
        ],
    );

    const refetchReviews = useCallback(
        async (signal?: AbortSignal) => {
            await executeRefetch({ signal, includeGroups: false });
        },
        [executeRefetch],
    );

    const refetchReviewsAndProducts = useCallback(
        async (signal?: AbortSignal) => {
            await executeRefetch({ signal, includeGroups: true });
        },
        [executeRefetch],
    );

    return {
        getProducts,
        getBrandGroups,
        refetchReviews,
        refetchReviewsAndProducts,
        isReviewsRefetchRunning,
    };
};
